/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext, process */
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';

import { isPending } from 'consts/requestStates';
import { TENANT_USER_ROLES } from 'consts/userRoles';
import Callout from 'components/Callout';
import FormError from 'components/form/FormError';
import SubmitButton from 'components/form/SubmitButton';
import TenantConfigFields from './TenantConfigFields';
import UsersFields from './UsersFields';
import { MARKETING_CAMPAIGNS } from '../../consts/marketingCampaigns';


const OWN_SUBDOMAIN = process.env.DJANGO_SITE_SUBDOMAIN;


export const CreateTenantForm = ({
  requestState,
  handleSubmit,
  onCancel,
  invalid,
  offices,
  userHasOffice,
  allowHttpProtocol,
  urlStyleIsCustom,
  displayUrl,
  setUserFieldCount,
  userCount,
  form,
  dispatch,
}) => {

  const handleOfficeChange = (event) => {
    const newValue = event.target.value;
    const selectedOffice = offices.find(office => office.get('id') === newValue);

    if (selectedOffice) {
      const disabledCampaigns = selectedOffice.get('default_disabled_campaigns') || [];

      // Update each marketing campaign field for the selected office
      Object.values(MARKETING_CAMPAIGNS).forEach(campaign => {
        // Enable the campaign checkbox if it's NOT in the disabled list
        dispatch(change(form, campaign, !disabledCampaigns.includes(campaign)));
      });
    }
  };

  return (
    <div>
      <form className="tenants-create-tenant-form" onSubmit={handleSubmit}>
        <span className="list-search-container pull-right">
          {!userHasOffice && (
            <div>
              <label htmlFor="office">{gettext('Affiliated Office')}</label>
              <FormError name="office" requestState={requestState} />
              <Field
                component="select"
                name="office"
                disabled={offices.isEmpty()}
                onChange={handleOfficeChange}
              >
                <option value="" disabled>{gettext('Please select an office')}</option>
                {offices
                  .sortBy(office => office.get('name'))
                  .map(office => (
                    <option key={office.get('id')} value={office.get('id')}>
                      {office.get('name')}
                    </option>
                  ))
                }
              </Field>
            </div>
          )}

          <label htmlFor="name">{gettext('Client Name')}</label>
          <FormError name="name" requestState={requestState} />
          <Field
            component="input"
            type="text"
            name="name"
            placeholder={gettext('A name for your client')}
          />

          <label htmlFor="salesforce_id">{gettext('SalesForce ID')}</label>
          <FormError name="salesforce_id" requestState={requestState} />
          <Field
            component="input"
            type="text"
            name="salesforce_id"
            placeholder={gettext('SalesForceID (optional)')}
          />

          <div>
            <FormError name="url_style" requestState={requestState} />
            <div>
              <label htmlFor="url_style">
                <Field name="url_style" component="input" type="radio" value="SUBDOMAIN" />
                {gettext('Subdomain')}
              </label>
              <label htmlFor="url_style">
                <Field name="url_style" component="input" type="radio" value="CUSTOM" />
                {gettext('Custom domain')}
              </label>
            </div>
          </div>

          {urlStyleIsCustom && (
            <Callout className="warning">
              <p>
                {gettext('If using a custom domain for this client, please ensure the domain ' +
                  'is registered and DNS has been configured appropriately before proceeding.')}
              </p>
            </Callout>
          )}

          <label htmlFor="url_fragment">
            {urlStyleIsCustom ? gettext('Domain') : gettext('Subdomain')}
          </label>
          <FormError name="url_fragment" requestState={requestState} />
          <Field
            name="url_protocol"
            component="select"
            className="col-url-protocol"
          >
            {allowHttpProtocol && <option value="http://">http://</option>}
            <option value="https://">https://</option>
          </Field>
          <Field
            component="input"
            type="text"
            name="url_fragment"
            placeholder={urlStyleIsCustom
              ? gettext('The URL of your client')
              : gettext('The subdomain of your client')
            }
            className="col-url-fragment"
          />

          <label htmlFor="display_url">
            {urlStyleIsCustom ? gettext('Display URL') : gettext('Domain')}
          </label>
          <FormError name="url" requestState={requestState} />
          <input type="url" disabled value={displayUrl} />

          <TenantConfigFields requestState={requestState} form={form} />

          <UsersFields
            requestState={requestState}
            roleOptions={TENANT_USER_ROLES}
            setUserFieldCount={setUserFieldCount}
            userCount={userCount}
            showEmailCheckbox
          />

          <FormError name="non_field_errors" requestState={requestState} />

          <button className="button secondary" onClick={onCancel} disabled={isPending(requestState)}>
            {gettext('Cancel')}
          </button>

          <SubmitButton
            valid={!invalid}
            onClick={handleSubmit}
            pending={isPending(requestState)}
            pendingText={gettext('Creating...')}
          >
            {gettext('Create')}
          </SubmitButton>

        </span>
      </form>
    </div>
  )
};

CreateTenantForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  requestState: ImmutablePropTypes.map.isRequired,
  offices: ImmutablePropTypes.list.isRequired,
  invalid: PropTypes.bool.isRequired,
  userHasOffice: PropTypes.bool.isRequired,
  urlStyleIsCustom: PropTypes.bool.isRequired,
  displayUrl: PropTypes.string.isRequired,
  setUserFieldCount: PropTypes.func.isRequired,
  userCount: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
};

const formName = 'createTenant';

const CreateTenantReduxForm = reduxForm({
  form: formName,
})(CreateTenantForm);

const selector = formValueSelector(formName);

function mapStateToProps(state, props) {
  const selectedOfficeId = selector(state, 'office');
  const { offices } = props;
  const selectedOffice = offices.find(office => office.get('id') === selectedOfficeId);
  const selectedOfficeCustomTenantDomain = selectedOffice
    && selectedOffice.get('has_custom_tenant_domain')
    && selectedOffice.get('custom_tenant_domain');

  const urlStyleIsCustom = selector(state, 'url_style') === 'CUSTOM';

  return {
    urlStyleIsCustom,
    displayUrl: selector(state, 'url_protocol') + selector(state, 'url_fragment') + (urlStyleIsCustom ? '' : `.${selectedOfficeCustomTenantDomain || OWN_SUBDOMAIN}`),
  };
}

export default connect(mapStateToProps)(CreateTenantReduxForm);
